import { DOCUMENT } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap } from "@angular/router";
import {
  ALLY_SERVICE,
  IAllyService,
  IAppDashboard,
  IPluginPermissions,
  ModelService,
  PermissionLoader,
  PluginInjectorService,
  PluginInjectorWidget,
  SessionFreshnessService,
  SessionService,
  WidgetDialogService,
  WidgetPermissions,
} from "core-client/api";
import { combineLatest, Subject, takeUntil } from "rxjs";

import { WIDGET_WINDOW_CHANNEL_FACTORY } from "../../../services/widget-window.service";
import { DashboardWidgetLoader } from "../../../shared/dashboard-widget-loader.service";
import { SharedModule } from "../../../shared/shared.module";
import { WidgetHeaderComponent } from "../../../shared/widget-header/widget-header.component";
import { WidgetHostRegistry } from "../../../shared/widget-host-registry.service";
import { WidgetWindowClient } from "./widget-window-client";

type WidgetWindowState = "loading" | "ready" | "not-found";

@Component({
  selector: "app-widget-window",
  imports: [SharedModule, WidgetHeaderComponent],
  providers: [WidgetHostRegistry],
  templateUrl: "./widget-window.component.html",
  styleUrl: "./widget-window.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WidgetWindowComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly sessionService = inject(SessionService);
  private readonly sessionFreshness = inject(SessionFreshnessService);
  private readonly modelService = inject(ModelService);
  private readonly pluginInjectorService = inject(PluginInjectorService);
  private readonly permissionLoader = inject(PermissionLoader);
  private readonly widgetLoader = inject(DashboardWidgetLoader);
  private readonly widgetPermissions = inject(WidgetPermissions);
  private readonly widgetDialogService = inject(WidgetDialogService);
  private readonly dialog = inject(MatDialog);
  private readonly document = inject(DOCUMENT);
  private readonly createChannel = inject(WIDGET_WINDOW_CHANNEL_FACTORY);
  private readonly allyService: IAllyService | null = inject(ALLY_SERVICE, { optional: true });
  private readonly ngUnsubscribe$ = new Subject<void>();
  private readonly onFullscreenChange = (): void => {
    this.isFullscreen = !!this.document.fullscreenElement;
  };
  private client: WidgetWindowClient | undefined;
  private permissions: IPluginPermissions[] = [];
  private menuOpen = false;

  state: WidgetWindowState = "loading";
  widget: PluginInjectorWidget.IWidget | undefined;
  isFullscreen = false;

  constructor() {
    this.allyService?.init();
  }

  ngOnInit(): void {
    this.document.addEventListener("fullscreenchange", this.onFullscreenChange);
    this.sessionService.user.loggedOut$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(() => this.closeWindow());
    void this.activatePlugins();
    this.sessionFreshness.start();
    combineLatest([this.route.paramMap, this.sessionService.dashboard.observeActiveAppDashboard$()])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(([params, dashboard]: [ParamMap, IAppDashboard]) => {
        void this.load(params.get("widgetId") ?? "", dashboard);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.client?.dispose();
  }

  private widgetHeaderPermitted(widget: PluginInjectorWidget.IWidget): boolean {
    const pluginPermissions = this.permissions.find(
      (candidate: IPluginPermissions) => candidate.plugin_id === widget.pluginId,
    );
    return pluginPermissions
      ? this.widgetPermissions.check(widget, pluginPermissions, "showHeader")
      : true;
  }

  renderWidgetHeader(widget: PluginInjectorWidget.IWidget): boolean {
    const header = widget.appPluginWidget.settings.widgetHeader;
    return this.widgetHeaderPermitted(widget) && (header.alwaysDisplayed || header.onHoverDisplayed);
  }

  showWidgetDivider(widget: PluginInjectorWidget.IWidget): boolean {
    return this.widgetHeaderPermitted(widget) && widget.appPluginWidget.settings.widgetHeader.alwaysDisplayed;
  }

  widgetHeaderIsHovered(widget: PluginInjectorWidget.IWidget): boolean {
    return widget.appPluginWidget.settings.widgetHeader.onHoverDisplayed && widget.isHeaderHovered;
  }

  canOpenSettings(widget: PluginInjectorWidget.IWidget): boolean {
    const pluginPermissions = this.permissions.find(
      (candidate: IPluginPermissions) => candidate.plugin_id === widget.pluginId,
    );
    return (
      this.widgetDialogService.isConfigurable(widget) &&
      !!pluginPermissions &&
      this.widgetPermissions.check(widget, pluginPermissions, "settingsEnabled")
    );
  }

  onHover(hovered: boolean): void {
    if (!this.widget || (!hovered && this.menuOpen)) {
      return;
    }
    this.widget.isHeaderHovered = hovered;
  }

  onMenuOpenChange(open: boolean): void {
    this.menuOpen = open;
    if (this.widget && open) {
      this.widget.isHeaderHovered = true;
    }
  }

  async toggleFullscreen(): Promise<void> {
    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen();
      return;
    }
    try {
      await this.document.documentElement.requestFullscreen();
    } catch (error) {
      const failure = error as Error;
      console.warn(`[WidgetWindow] fullscreen refused: ${failure.message}`);
    }
  }

  openSettings(widget: PluginInjectorWidget.IWidget): void {
    void this.widgetDialogService.loadSettingComponent(
      widget,
      this.modelService,
      this.pluginInjectorService,
      this.dialog,
    );
  }

  closeWindow(): void {
    this.document.defaultView?.close();
  }

  private async activatePlugins(): Promise<void> {
    try {
      await this.modelService._appGlobalSettings.initialize();
    } catch (error) {
      console.error("Failed to initialize global settings:", error);
    }
    await this.pluginInjectorService.whenPluginsReady();
    for (const plugin of this.pluginInjectorService.getPlugins()) {
      this.modelService._plugin.notifyPlugin(plugin, "activate");
    }
  }

  private async load(widgetId: string, dashboard: IAppDashboard): Promise<void> {
    const user = this.sessionService.user.getUser();
    if (!user) {
      return;
    }
    const [permissions, appPluginWidgets, appPlugins] = await Promise.all([
      this.permissionLoader.load(dashboard, user),
      this.modelService._user.listAllAppPluginWidgets(user._id),
      this.modelService._appPlugin.listAll(),
    ]);
    this.permissions = permissions;
    const widget = this.widgetLoader
      .buildWidgets(appPluginWidgets, appPlugins, user, permissions)
      .find((candidate: PluginInjectorWidget.IWidget) => candidate._id === widgetId);

    if (!widget) {
      console.warn(`[WidgetWindow] widget ${widgetId} is not available on dashboard "${dashboard.name}"`);
      this.widget = undefined;
      this.state = "not-found";
      return;
    }
    if (this.widget?._id === widget._id) {
      this.widget.appPluginWidget = widget.appPluginWidget;
      this.widget.name = widget.name;
      return;
    }
    this.widget = widget;
    this.state = "ready";
    this.document.title = widget.name;
    this.announce(widget._id);
  }

  private announce(widgetId: string): void {
    const view = this.document.defaultView;
    if (!view || typeof BroadcastChannel === "undefined") {
      return;
    }
    this.client?.dispose();
    this.client = new WidgetWindowClient(widgetId, this.createChannel(), view);
    this.client.announce();
    console.debug(`[WidgetWindow] showing widget ${widgetId} in its own window`);
  }
}
